import { useLocalStorage } from '@backoffice-frontend/hooks'

/**
 * The metropolis last picked in any TMP area of the Admin Panel. One key for all of them, so switching between
 * Vehicle Monitoring and Route Builder keeps the same metro. It lives in localStorage and is not one of the
 * user-specific keys `AuthContext` resets, so it survives a logout and a different user signing in on the machine.
 */
export const TMP_METROPOLIS_STORAGE_KEY = 'adminPanel_tmp_selectedMetropolisId'

/** The metropolis assumed before the list has loaded and nothing was picked yet. */
const FALLBACK_METROPOLIS_ID = 1

/**
 * The metropolis to show: the stored one while it is still offered, otherwise the first one.
 *
 * Until the list has loaded the stored id is trusted as is, so the area doesn't fetch the first metro and then
 * jump to the stored one. A stored id the list no longer contains falls back instead of querying a dead metro.
 */
export const resolveTmpMetropolisId = (
  storedId: number | null,
  metropolises: ReadonlyArray<{ id: string | number }>,
): number => {
  const isOffered =
    metropolises.length === 0 ||
    metropolises.some(metropolis => Number(metropolis.id) === storedId)
  if (storedId !== null && isOffered) {
    return storedId
  }
  return Number(metropolises[0]?.id) || FALLBACK_METROPOLIS_ID
}

/** The TMP metropolis picker state, persisted on the machine across sessions and logouts. */
export const useTmpMetropolisId = (
  metropolises: ReadonlyArray<{ id: string | number }>,
): [number, (metropolisId: number) => void] => {
  const [storedId, setStoredId] = useLocalStorage<number | null>(
    TMP_METROPOLIS_STORAGE_KEY,
    null,
  )
  return [resolveTmpMetropolisId(storedId, metropolises), setStoredId]
}
